import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Save, Users, MessageCircle, Coins, TrendingUp, Loader2, Download, ThumbsUp, ThumbsDown, BookOpen } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { can } from '../../utils/access';
import { PageHeader, AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppButton, StatsCard, StatsGrid, FormActionBar, ResetButton } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import './ai-doubt-settings.scss';

const DEFAULT_SETTINGS = {
  enabled: false,
  studentMonthlyTokenLimit: 50000,
  maxSessionsPerDay: 10,
  maxMessagesPerSession: 50,
  allowedSubjects: [],
  customSystemPrompt: '',
  model: 'gpt-4o-mini',
};

const AIDoubtSettings = () => {
  const navigate = useNavigate();
  const canManage = can('ai.manage');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [enabledModule, setEnabledModule] = useState(true);
  // Pristine snapshot (last loaded / saved) used by the Reset action.
  const pristineRef = useRef({ settings: DEFAULT_SETTINGS, enabledModule: true });
  const [analytics, setAnalytics] = useState(null);
  const [topicData, setTopicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [subjectInput, setSubjectInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, analyticsRes, topicRes] = await Promise.all([
        apiClient.get('/ai-doubt/admin/settings'),
        apiClient.get('/ai-doubt/admin/analytics').catch(() => ({ data: null })),
        apiClient.get('/ai-doubt/admin/topic-analytics').catch(() => ({ data: null })),
      ]);
      const loadedSettings = settingsRes.data.aiDoubt || DEFAULT_SETTINGS;
      const loadedEnabled = settingsRes.data.enabledModule !== undefined ? settingsRes.data.enabledModule : true;
      setSettings(loadedSettings);
      setEnabledModule(loadedEnabled);
      pristineRef.current = { settings: loadedSettings, enabledModule: loadedEnabled };
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
      if (topicRes.data) setTopicData(topicRes.data);
    } catch {
      setToast({ show: true, message: 'Failed to load settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/ai-doubt/admin/settings', { ...settings, enabledModule });
      // The saved values become the new pristine baseline for Reset.
      pristineRef.current = { settings, enabledModule };
      setToast({ show: true, message: 'Settings saved successfully', type: 'success' });
    } catch {
      setToast({ show: true, message: 'Failed to save settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(pristineRef.current.settings);
    setEnabledModule(pristineRef.current.enabledModule);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const response = await apiClient.get(`/ai-doubt/admin/export?format=csv`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-doubt-logs-${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const { data } = await apiClient.get('/ai-doubt/admin/export?format=json');
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-doubt-logs-${Date.now()}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      setToast({ show: true, message: `Exported as ${format.toUpperCase()} successfully`, type: 'success' });
    } catch {
      setToast({ show: true, message: 'Export failed', type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const addSubject = () => {
    const val = subjectInput.trim();
    if (val && !settings.allowedSubjects.includes(val)) {
      setSettings(prev => ({ ...prev, allowedSubjects: [...prev.allowedSubjects, val] }));
      setSubjectInput('');
    }
  };

  const removeSubject = (subject) => {
    setSettings(prev => ({ ...prev, allowedSubjects: prev.allowedSubjects.filter(s => s !== subject) }));
  };

  if (loading) {
    return (
      <div className="sl-page">
        <PageHeader icon={Sparkles} title="AIvo - AI Doubt Assistant" description="Configure AI-powered doubt solving for students" />
        <div className="aid-settings-loading"><Loader2 size={24} className="aid-spin" /> Loading...</div>
      </div>
    );
  }

  const maxTopicCount = topicData?.topics?.length > 0 ? Math.max(...topicData.topics.map(t => t.count)) : 1;

  return (
    <div className="sl-page has-form-actionbar">
      <PageHeader
        icon={Sparkles}
        title="AIvo - AI Doubt Assistant"
        description="Configure AI-powered doubt solving for students"
        actions={
          <div className="aid-header-actions-row">
            <AppButton variant="outline" onClick={() => handleExport('csv')} disabled={exporting}>
              <Download size={15} /> CSV
            </AppButton>
            <AppButton variant="outline" onClick={() => handleExport('json')} disabled={exporting}>
              <Download size={15} /> JSON
            </AppButton>
          </div>
        }
      />

      {/* Analytics Cards */}
      {analytics && (
        <StatsGrid className="aid-stats-grid">
          <StatsCard title="Total Tokens Used" value={(analytics.totalTokens || 0).toLocaleString()} icon={Coins} iconColor="blue" />
          <StatsCard title="Active Students" value={analytics.activeStudents || 0} icon={Users} iconColor="emerald" />
          <StatsCard title="Total Messages" value={analytics.totalMessages || 0} icon={MessageCircle} iconColor="violet" />
          <StatsCard title="Total Sessions" value={analytics.totalSessions || 0} icon={TrendingUp} iconColor="amber" />
        </StatsGrid>
      )}

      {/* Feedback + Topic Analytics row */}
      {topicData && (
        <div className="aid-analytics-row">
          {/* Feedback Stats */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>AI Quality Feedback</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="aid-feedback-stats">
                <div className="aid-feedback-stat positive">
                  <ThumbsUp size={20} />
                  <div>
                    <span className="aid-feedback-count">{topicData.feedback?.thumbsUp || 0}</span>
                    <span className="aid-feedback-label">Helpful</span>
                  </div>
                </div>
                <div className="aid-feedback-stat negative">
                  <ThumbsDown size={20} />
                  <div>
                    <span className="aid-feedback-count">{topicData.feedback?.thumbsDown || 0}</span>
                    <span className="aid-feedback-label">Not Helpful</span>
                  </div>
                </div>
                {(topicData.feedback?.thumbsUp > 0 || topicData.feedback?.thumbsDown > 0) && (
                  <div className="aid-feedback-ratio">
                    <div className="aid-feedback-bar">
                      <div
                        className="aid-feedback-bar-fill positive"
                        style={{ width: `${Math.round((topicData.feedback.thumbsUp / (topicData.feedback.thumbsUp + topicData.feedback.thumbsDown)) * 100)}%` }}
                      />
                    </div>
                    <span>{Math.round((topicData.feedback.thumbsUp / (topicData.feedback.thumbsUp + topicData.feedback.thumbsDown)) * 100)}% satisfaction</span>
                  </div>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          {/* Topic Distribution */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Topic Distribution — {topicData.month}</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              {topicData.topics?.length > 0 ? (
                <div className="aid-topic-list">
                  {topicData.topics.slice(0, 8).map((t, i) => (
                    <div key={i} className="aid-topic-row">
                      <div className="aid-topic-name">
                        <BookOpen size={13} />
                        <span>{t.name}</span>
                      </div>
                      <div className="aid-topic-bar-wrap">
                        <div className="aid-topic-bar">
                          <div className="aid-topic-bar-fill" style={{ width: `${(t.count / maxTopicCount) * 100}%` }} />
                        </div>
                        <span className="aid-topic-count">{t.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="aid-help-text">No topic data available yet this month.</p>
              )}
            </AppCardContent>
          </AppCard>
        </div>
      )}

      {/* Daily Trend */}
      {topicData?.dailyTrend?.length > 0 && (
        <AppCard className="aid-trend-card">
          <AppCardHeader>
            <AppCardTitle>Daily Usage Trend (Last 30 Days)</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="aid-trend-chart">
              {(() => {
                const maxSessions = Math.max(...topicData.dailyTrend.map(d => d.sessions));
                return topicData.dailyTrend.map((d, i) => (
                  <div key={i} className="aid-trend-bar-group" title={`${d._id}: ${d.sessions} sessions, ${d.tokens} tokens`}>
                    <div className="aid-trend-bar" style={{ height: `${maxSessions > 0 ? (d.sessions / maxSessions) * 100 : 0}%` }} />
                    <span className="aid-trend-date">{d._id.slice(8)}</span>
                  </div>
                ));
              })()}
            </div>
          </AppCardContent>
        </AppCard>
      )}

      <div className="aid-settings-grid">
        {/* Main Settings Card */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>General Settings</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>Enable AI Doubt Solving</span>
                <small>Allow students to use AIvo, the AI doubt assistant</small>
              </div>
              <label className="aid-toggle">
                <input type="checkbox" checked={settings.enabled} onChange={e => setSettings(prev => ({ ...prev, enabled: e.target.checked }))} />
                <span className="aid-toggle-slider" />
              </label>
            </div>

            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>Show in Sidebar</span>
                <small>Enable/disable the module visibility for students</small>
              </div>
              <label className="aid-toggle">
                <input type="checkbox" checked={enabledModule} onChange={e => setEnabledModule(e.target.checked)} />
                <span className="aid-toggle-slider" />
              </label>
            </div>

            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>AI Model</span>
                <small>Select the AI model for responses</small>
              </div>
              <select className="aid-select" value={settings.model} onChange={e => setSettings(prev => ({ ...prev, model: e.target.value }))}>
                <option value="gpt-4o-mini">GPT-4o Mini (Recommended)</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>
          </AppCardContent>
        </AppCard>

        {/* Limits Card */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Usage Limits</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>Monthly Token Limit (per student)</span>
                <small>~500 tokens per message exchange</small>
              </div>
              <input
                type="number"
                className="aid-number-input"
                value={settings.studentMonthlyTokenLimit}
                onChange={e => setSettings(prev => ({ ...prev, studentMonthlyTokenLimit: parseInt(e.target.value) || 50000 }))}
                min={1000}
                max={1000000}
                step={5000}
              />
            </div>

            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>Max Sessions Per Day</span>
                <small>Limit new conversations per student per day</small>
              </div>
              <input
                type="number"
                className="aid-number-input"
                value={settings.maxSessionsPerDay}
                onChange={e => setSettings(prev => ({ ...prev, maxSessionsPerDay: parseInt(e.target.value) || 10 }))}
                min={1}
                max={100}
              />
            </div>

            <div className="aid-setting-row">
              <div className="aid-setting-label">
                <span>Max Messages Per Session</span>
                <small>Limit messages in a single conversation</small>
              </div>
              <input
                type="number"
                className="aid-number-input"
                value={settings.maxMessagesPerSession}
                onChange={e => setSettings(prev => ({ ...prev, maxMessagesPerSession: parseInt(e.target.value) || 50 }))}
                min={5}
                max={200}
              />
            </div>
          </AppCardContent>
        </AppCard>

        {/* Subjects Card */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Allowed Subjects (Optional)</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <p className="aid-help-text">Leave empty to allow all educational topics. Add subjects to restrict AI to specific areas only.</p>
            {canManage && (
              <div className="aid-subject-input">
                <input
                  type="text"
                  placeholder="Type a subject and press Enter"
                  value={subjectInput}
                  onChange={e => setSubjectInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubject(); } }}
                />
                <AppButton variant="outline" size="sm" onClick={addSubject}>Add</AppButton>
              </div>
            )}
            {settings.allowedSubjects.length > 0 && (
              <div className="aid-subjects-list">
                {settings.allowedSubjects.map(s => (
                  <span key={s} className="aid-subject-tag">
                    {s}
                    {canManage && <button onClick={() => removeSubject(s)}>&times;</button>}
                  </span>
                ))}
              </div>
            )}
          </AppCardContent>
        </AppCard>

        {/* Custom Prompt Card */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Custom AI Instructions (Optional)</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <p className="aid-help-text">Add custom instructions for the AI. For example: "Focus on practical examples" or "Always include code snippets".</p>
            <textarea
              className="aid-textarea"
              rows={4}
              maxLength={1000}
              placeholder="e.g., Always respond in Hindi when the student asks in Hindi. Focus on practical examples with real-world scenarios."
              value={settings.customSystemPrompt}
              onChange={e => setSettings(prev => ({ ...prev, customSystemPrompt: e.target.value }))}
            />
            <small className="aid-char-count">{(settings.customSystemPrompt || '').length}/1000</small>
          </AppCardContent>
        </AppCard>
      </div>

      {/* Top Users Table */}
      {analytics?.topUsers?.length > 0 && (
        <AppCard className="aid-top-users-card">
          <AppCardHeader>
            <AppCardTitle>Top Students by Usage — {analytics.month}</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="aid-table-wrap">
              <table className="aid-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Tokens Used</th>
                    <th>Messages</th>
                    <th>Sessions</th>
                    <th>Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.topUsers.map((u, i) => (
                    <tr key={i}>
                      <td>{u.student?.name || 'Unknown'}</td>
                      <td>{(u.tokensUsed || 0).toLocaleString()}</td>
                      <td>{u.messagesCount || 0}</td>
                      <td>{u.sessionsCreated || 0}</td>
                      <td>
                        <div className="aid-usage-cell">
                          <div className="aid-usage-cell-bar">
                            <div className="aid-usage-cell-fill" style={{ width: `${Math.min(u.percentUsed, 100)}%` }} />
                          </div>
                          <span>{u.percentUsed}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {canManage && (
        <FormActionBar>
          <AppButton variant="outline" type="button" onClick={() => navigate(-1)}>Cancel</AppButton>
          <ResetButton onReset={handleReset} disabled={saving} />
          <AppButton onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={16} className="aid-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </AppButton>
        </FormActionBar>
      )}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, show: false }))} />
    </div>
  );
};

export default AIDoubtSettings;
